/**
 * Provider-neutral errors.
 *
 * Firebase throws FirebaseError, a REST client throws on HTTP status, a socket
 * throws on disconnect. The UI should not have to know the difference, so every
 * provider translates its failures into a ZivError with one of these codes.
 */

export const ErrorCode = {
  NETWORK:        'network',
  UNAUTHENTICATED:'unauthenticated',
  PERMISSION:     'permission',
  NOT_FOUND:      'not-found',
  INVALID_INPUT:  'invalid-input',
  // Someone else got there first — a channel handle already taken, say. The
  // input is valid, so INVALID_INPUT would send the user looking for a typo.
  CONFLICT:       'conflict',
  RATE_LIMITED:   'rate-limited',
  CODE_INVALID:   'code-invalid',
  CODE_EXPIRED:   'code-expired',
  QUOTA:          'quota',
  UPLOAD_FAILED:  'upload-failed',
  NOT_IMPLEMENTED:'not-implemented',
  UNKNOWN:        'unknown'
}

export class ZivError extends Error {
  /**
   * @param {string} code  One of ErrorCode
   * @param {string} message  Safe to show a user
   * @param {object} [options]
   * @param {unknown} [options.cause] The original provider error, for logging
   */
  constructor(code, message, { cause } = {}) {
    super(message)
    this.name = 'ZivError'
    this.code = code
    this.cause = cause
  }

  get isRetryable() {
    return this.code === ErrorCode.NETWORK || this.code === ErrorCode.RATE_LIMITED
  }
}

/** Thrown by every stub in services/rest until that provider is built. */
export function notImplemented(method) {
  return new ZivError(
    ErrorCode.NOT_IMPLEMENTED,
    `${method} is not implemented in the REST provider yet. ` +
    `Set VITE_BACKEND=firebase or VITE_BACKEND=mock, or see docs/MIGRATION-50K.md.`
  )
}

/** Wording the UI can show directly. */
export function userMessage(error) {
  if (!(error instanceof ZivError)) return 'Something went wrong. Please try again.'
  switch (error.code) {
    case ErrorCode.NETWORK:         return 'No connection. Check your internet and try again.'
    case ErrorCode.UNAUTHENTICATED: return 'You are signed out. Please sign in again.'
    case ErrorCode.PERMISSION:      return 'You do not have access to this.'
    case ErrorCode.NOT_FOUND:       return 'This is no longer available.'
    case ErrorCode.INVALID_INPUT:   return 'Please check what you entered.'
    case ErrorCode.RATE_LIMITED:    return 'Too many attempts. Please wait a moment.'
    case ErrorCode.CODE_INVALID:    return 'That code is not correct.'
    case ErrorCode.CODE_EXPIRED:    return 'That code has expired. Request a new one.'
    case ErrorCode.QUOTA:           return 'Service temporarily unavailable. Please try later.'
    case ErrorCode.UPLOAD_FAILED:   return 'Upload failed. Please try again.'
    default:                        return error.message || 'Something went wrong.'
  }
}
